"use client";

import { useId, useState } from "react";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import styles from "./explorers.module.css";

const LAYERS = [
  {
    label: "Meaning",
    title: "Which definition did we use?",
    body: "A product citation identifies the published definition and the relation used to answer. It gives the meaning a version you can inspect.",
    rows: [
      ["Definition", "Net revenue retention"],
      ["Version", "v3 · illustrative"],
      ["Basis", "Opening customer cohort; expansion and churn included"],
    ],
  },
  {
    label: "Execution",
    title: "What actually ran?",
    body: "An execution certificate is a separate evidence layer. It binds a particular invocation to the queried tables, engine and result identity.",
    rows: [
      ["Invocation", "One run · one attempt · one tool call"],
      ["Recorded", "Queried tables and SQL hash"],
      ["Scope", "A recorded execution, not the whole conversation"],
    ],
  },
  {
    label: "Result",
    title: "Which result are we looking at?",
    body: "Canonical results preserve types, decimal scale and a deterministic row order. A hash can then identify those exact result bytes.",
    rows: [
      ["Value", "118.0%"],
      ["Representation", "Decimal · explicit scale"],
      ["Proves", "Result identity and integrity"],
    ],
  },
  {
    label: "Limits",
    title: "Where does the proof stop?",
    body: "The receipt does not establish that the business definition is correct. A precisely recorded answer can still be based on the wrong meaning.",
    rows: [
      ["Semantic truth", "Requires accountable review"],
      ["Source-row drill-down", "Only where lineage was retained"],
      ["Evidence timing", "Observed timing is not enforced timing"],
    ],
  },
] as const;

export default function ReceiptExplorer() {
  const [open, setOpen] = useState(false);
  const [layer, setLayer] = useState(0);
  const id = useId();
  const selected = LAYERS[layer]!;
  return (
    <div className={styles.explorer}>
      <div className={styles.eyebrow}>
        <TwinMark /> 01 / Inspect the evidence
      </div>
      <h3 className={styles.heading}>The number is only the beginning.</h3>
      <p className={styles.intro}>
        Both answers say net revenue retention was 118%. What can you check
        behind each one?
      </p>
      <div className={styles.compare}>
        <div className={styles.answer}>
          <span className={styles.answerLabel}>Answer alone</span>
          <strong className={styles.number}>
            118<small>%</small>
          </strong>
          <p>“Net revenue retention was 118% in Q2.”</p>
          <span className={styles.quiet}>No supporting evidence shown.</span>
        </div>
        <div className={styles.answer}>
          <span className={styles.answerLabel}>Answer with evidence</span>
          <strong className={styles.number}>
            118<small>%</small>
          </strong>
          <p>“Net revenue retention was 118% in Q2.”</p>
          <button
            className={styles.button}
            aria-expanded={open}
            aria-controls={id}
            onClick={() => setOpen((value) => !value)}
          >
            {open ? "Close the receipt −" : "Open the receipt +"}
          </button>
        </div>
      </div>
      <div id={id} hidden={!open} className={styles.receipt}>
        <div
          className={styles.choices}
          role="group"
          aria-label="Receipt layers"
        >
          {LAYERS.map((item, i) => (
            <button
              key={item.label}
              aria-pressed={layer === i}
              onClick={() => setLayer(i)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className={styles.detail} aria-live="polite" aria-atomic="true">
          <h4>{selected.title}</h4>
          <p>{selected.body}</p>
          <dl className={styles.ledger}>
            {selected.rows.map(([key, value]) => (
              <div key={key}>
                <dt>{key}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
      <div className={styles.limit}>
        Illustrative evidence model. Product citations and execution
        certificates are separate artefacts; a query response does not
        necessarily expose both. Evidence makes claims checkable, not
        automatically correct.
      </div>
    </div>
  );
}
